import React from 'react';
import { Card, Row, Col, Table, Badge } from 'react-bootstrap';
import { formatQuantityWithCases } from '../utils/commonUtils';

/**
 * ReorderAlertDetailRow - Expandable detail panel for reorder alert
 *
 * Displays comprehensive information about a reorder alert including:
 * - Product information and image
 * - Current status with human-readable summary
 * - List of confirmed shipments with ETAs
 * - Sales breakdown and trend analysis
 * - Actionable recommendations
 */
const ReorderAlertDetailRow = ({ alert }) => {
    const { details } = alert;

    if (!details) return null;

    // Format number with commas
    const formatNumber = (num) => {
        if (num === null || num === undefined) return 'N/A';
        return Math.round(num).toLocaleString();
    };

    // Get case_pack from details
    const casePack = details.stock_item?.case_pack || 1;

    // Helper for formatting quantities with cases
    const formatQty = (qty, options = {}) => formatQuantityWithCases(qty, casePack, options);

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Get severity config
    const getSeverityConfig = (severity) => {
        const config = {
            critical: { emoji: '🔴', color: 'danger', text: 'Critical' },
            warning: { emoji: '🟠', color: 'warning', text: 'Warning' },
            watch: { emoji: '🟡', color: 'info', text: 'Watch' }
        };
        return config[severity] || config.watch;
    };

    // Get trend indicator
    const getTrendIndicator = (direction) => {
        const indicators = {
            up: { icon: '↑', color: 'danger', text: 'Trending Up' },
            down: { icon: '↓', color: 'success', text: 'Trending Down' },
            stable: { icon: '→', color: 'secondary', text: 'Stable' },
            'n/a': { icon: '-', color: 'muted', text: 'N/A' }
        };
        return indicators[direction] || indicators['n/a'];
    };

    const severityConfig = getSeverityConfig(alert.severity);
    const trendIndicator = getTrendIndicator(details.trend_details?.direction);

    // Generate human-readable current status summary
    const generateStatusSummary = () => {
        const { severity, current_inventory, arriving_shipments, projected_stock, critical_stock_level, lead_time_days } = alert;
        const config = getSeverityConfig(severity);

        return (
            <p className="mb-0">
                <strong className={`text-${config.color}`}>{config.emoji} {config.text}:</strong> Current stock is <strong>{formatQty(current_inventory)}</strong>.
                {arriving_shipments > 0 ? (
                    <> With <strong>{formatQty(arriving_shipments)}</strong> arriving, </>
                ) : (
                    <> With no confirmed shipments arriving, </>
                )}
                projected stock will be <strong className={`text-${config.color}`}>{formatQty(projected_stock)}</strong> after {lead_time_days} days (lead time).
                {projected_stock < critical_stock_level ? (
                    <> This is <strong className={`text-${config.color}`}>below</strong> your critical level of <strong>{formatQty(critical_stock_level)}</strong>.</>
                ) : (
                    <> This is approaching your critical level of <strong>{formatQty(critical_stock_level)}</strong>.</>
                )}
            </p>
        );
    };

    // Generate recommendation message
    const generateRecommendation = () => {
        const { severity, recommended_order_quantity, days_until_critical } = alert;

        if (severity === 'critical') {
            return (
                <div className="alert alert-danger mb-0">
                    <i className="material-icons" style={{ fontSize: '20px', verticalAlign: 'middle' }}>error</i>
                    {' '}<strong>Urgent Action Required:</strong> Order <strong>{formatQty(recommended_order_quantity)}</strong> immediately to avoid stockout.
                    Stock may run out within <strong>{days_until_critical}</strong> days at current usage rate.
                </div>
            );
        } else if (severity === 'warning') {
            return (
                <div className="alert alert-warning mb-0">
                    <i className="material-icons" style={{ fontSize: '20px', verticalAlign: 'middle' }}>warning</i>
                    {' '}<strong>Action Needed:</strong> Order <strong>{formatQty(recommended_order_quantity)}</strong> within <strong>{days_until_critical}</strong> days to maintain stock levels.
                </div>
            );
        } else {
            return (
                <div className="alert alert-info mb-0">
                    <i className="material-icons" style={{ fontSize: '20px', verticalAlign: 'middle' }}>info</i>
                    {' '}<strong>Prepare to Order:</strong> Consider ordering <strong>{formatQty(recommended_order_quantity)}</strong> within <strong>{days_until_critical}</strong> days to stay ahead of demand.
                </div>
            );
        }
    };

    return (
        <div className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
            <Row className="g-3">
                <Col>
                    {/* Current Status Card */}
                    <Card className="mb-3">
                        <Card.Header className={`bg-${severityConfig.color} text-white`}>
                            <strong>Current Status</strong>
                        </Card.Header>
                        <Card.Body>
                            {generateStatusSummary()}
                            {(alert.critical_level_is_dynamic || alert.lead_time_is_default) && (
                                <div className="mt-3 pt-2 border-top">
                                    <small className="text-muted">
                                        <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle' }}>info</i>
                                        {' '}<strong>Note:</strong>{' '}
                                        {alert.critical_level_is_dynamic && (
                                            <span>Critical Level was calculated dynamically as Daily Usage × Lead Time × 1.5. </span>
                                        )}
                                        {alert.lead_time_is_default && (
                                            <span>Lead Time is using the default value of 30 days (not configured for this manufacturer). </span>
                                        )}
                                        Consider configuring these values for more accurate alerts.
                                    </small>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Confirmed Shipments Card */}
                    <Card className="mb-3">
                        <Card.Header className="bg-success text-white">
                            <strong>Confirmed Shipments</strong>
                        </Card.Header>
                        <Card.Body>
                            {details.confirmed_shipments && details.confirmed_shipments.length > 0 ? (
                                <>
                                    <Table striped bordered hover size="sm">
                                        <thead>
                                            <tr>
                                                <th>PO Number</th>
                                                <th>Vendor</th>
                                                <th>Quantity</th>
                                                <th>ETA</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {details.confirmed_shipments.map((shipment, index) => (
                                                <tr key={index}>
                                                    <td>{shipment.po_number}</td>
                                                    <td>{shipment.vendor_name}</td>
                                                    <td><strong>{formatQty(shipment.quantity)}</strong></td>
                                                    <td>{formatDate(shipment.eta)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <div className="text-end">
                                        <strong>Total Arriving: {formatQty(alert.arriving_shipments)}</strong>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center text-muted py-3">
                                    <i className="material-icons" style={{ fontSize: '36px' }}>local_shipping</i>
                                    <p className="mb-0 mt-2">No confirmed shipments scheduled</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Sales Analysis Card */}
                    <Card className="mb-3">
                        <Card.Header className="bg-info text-white">
                            <strong>Sales Analysis</strong>
                        </Card.Header>
                        <Card.Body>
                            {details.sales_breakdown ? (
                                <Table size="sm" borderless className="mb-0">
                                    <tbody>
                                        <tr>
                                            <td className="text-muted">Recent 30 days:</td>
                                            <td>
                                                <strong>{formatQty(details.sales_breakdown.recent_30_days)}</strong>
                                                <span className="text-muted"> (avg {formatQty(details.sales_breakdown.recent_daily_avg, { isRate: true })})</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">
                                                {details.sales_breakdown.comparison_type === 'year_over_year' ? (
                                                    'Same period last year:'
                                                ) : (
                                                    'Days 31-90:'
                                                )}
                                            </td>
                                            <td>
                                                <strong>{formatQty(details.sales_breakdown.comparison_sales || details.sales_breakdown.days_31_to_90)}</strong>
                                                {details.sales_breakdown.comparison_type === 'year_over_year' ? (
                                                    <span className="text-muted"> (year-over-year comparison)</span>
                                                ) : (
                                                    <span className="text-muted"> (avg {formatQty(details.sales_breakdown.older_daily_avg, { isRate: true })})</span>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Weighted average:</td>
                                            <td><strong>{formatQty(details.sales_breakdown.weighted_daily_avg, { isRate: true })}</strong></td>
                                        </tr>
                                        <tr>
                                            <td className="text-muted">Trend adjustment:</td>
                                            <td>
                                                <Badge bg={details.sales_breakdown.trend_adjustment_factor > 1 ? 'danger' : details.sales_breakdown.trend_adjustment_factor < 1 ? 'success' : 'secondary'}>
                                                    ×{details.sales_breakdown.trend_adjustment_factor}
                                                </Badge>
                                                {details.sales_breakdown.trend_adjustment_factor > 1 && ' (trending up)'}
                                                {details.sales_breakdown.trend_adjustment_factor < 1 && ' (trending down)'}
                                                {details.sales_breakdown.trend_adjustment_factor === 1 && ' (stable)'}
                                            </td>
                                        </tr>
                                        <tr className="border-top">
                                            <td className="text-muted"><strong>Final daily usage:</strong></td>
                                            <td><strong className="text-primary">{formatQty(details.sales_breakdown.adjusted_daily_usage, { isRate: true })}</strong></td>
                                        </tr>
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted mb-0">No sales data available</p>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Trend Details Card */}
                    <Card className="mb-3">
                        <Card.Header className="bg-warning text-dark">
                            <strong>Trend Analysis</strong>
                        </Card.Header>
                        <Card.Body>
                            {details.trend_details ? (
                                <div>
                                    <div className="d-flex align-items-center mb-3">
                                        <div
                                            className={`text-${trendIndicator.color}`}
                                            style={{ fontSize: '48px', marginRight: '15px' }}
                                        >
                                            {trendIndicator.icon}
                                        </div>
                                        <div>
                                            <h5 className={`text-${trendIndicator.color} mb-1`}>
                                                {trendIndicator.text}
                                            </h5>
                                            <p className="mb-0">
                                                {details.trend_details.percentage !== null ? (
                                                    <>
                                                        <strong className={`text-${trendIndicator.color}`}>
                                                            {details.trend_details.percentage > 0 ? '+' : ''}
                                                            {details.trend_details.percentage}%
                                                        </strong>
                                                        {' '}change
                                                    </>
                                                ) : (
                                                    'Insufficient data for trend calculation'
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-muted small">
                                        <strong>Comparison method:</strong>{' '}
                                        {details.trend_details.comparison_type === 'year_over_year' ? (
                                            <span className="text-primary">Year-over-year (seasonal)</span>
                                        ) : (
                                            <span>Recent vs older period</span>
                                        )}
                                        <br />
                                        <strong>Comparison period:</strong> vs {formatDate(details.trend_details.comparison_period_start)} - {formatDate(details.trend_details.comparison_period_end)}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-muted mb-0">No trend data available</p>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Recommendation Card */}
                    <Card>
                        <Card.Header className="bg-dark text-white">
                            <strong>Recommendation</strong>
                        </Card.Header>
                        <Card.Body>
                            {generateRecommendation()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ReorderAlertDetailRow;
